import { Col, Row, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import moment from 'moment';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation } from '../../../helpers/formatters';
import { getAppointmentTypeName } from '../../../helpers/utils';
import {
  Resource_AppointmentLocation,
  Resource_AppointmentTypeMsg,
  useGetAvailableAppointmentSlotsQuery,
} from '../../../src/generated/graphql';
import { AveliosButton } from '../../common-layout/avelios-button';
import { AveliosSpinner } from '../../loading/avelios-spinner';
import styles from '../facility-selection.module.scss';

interface SlotRequestProps {
  directBooking: boolean;
  clinicId: Maybe<string> | undefined;
  facilityId: Maybe<string> | undefined;
  officeHourId: Maybe<string> | undefined;
  appointmentLocation: Maybe<Resource_AppointmentLocation> | undefined;
  appointmentType: Maybe<Resource_AppointmentTypeMsg> | undefined;
}

export const SlotRequest: FC<SlotRequestProps> = ({
  clinicId,
  officeHourId,
  facilityId,
  appointmentType,
  directBooking,
  appointmentLocation,
}) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  const { data: availableAppointmentSlots, loading: slotsLoading } =
    useGetAvailableAppointmentSlotsQuery({
      skip: !clinicId || !officeHourId || !appointmentType,
      fetchPolicy: 'network-only',
      variables: {
        clinicId: clinicId!,
        request: {
          start: { seconds: moment()?.startOf('day').unix() },
          end: { seconds: moment().add(6, 'months')?.endOf('day').unix() },
          duration: appointmentType?.duration,
          officeHourId,
          maxNumberOfResults: 100,
          appointmentTypeId: appointmentType?.id,
          patientBookable: true,
        },
      },
    });
  const availableSlots =
    availableAppointmentSlots?.getAvailableAppointmentSlots?.appointmentTimeFrames?.map(
      (slot, index) => ({ slot, index })
    );

  // NOTE: It is not important to randomly select one here
  // as this will be resolved in the slot selection
  const distinctSlots: {
    startTimeSeconds: number;
  }[] = [];
  availableSlots?.forEach((slot) => {
    const startTime = slot.slot!.startTime!.seconds!;
    if (!distinctSlots.find((slot) => slot.startTimeSeconds === startTime)) {
      distinctSlots.push({ startTimeSeconds: startTime });
    }
  });

  const noAppointmentsAvailable = distinctSlots.length === 0;

  const appointmentTypeName = extractTranslation(
    getAppointmentTypeName(appointmentType),
    router.locale
  );

  return (
    <div className={styles.nextAvailableAppointmentsWrapper}>
      <Title level={3}>
        {formatMessage({
          id: 'facility-selection.next-available-appointments',
        })}
        {appointmentType && ` | ${appointmentTypeName}`}
      </Title>

      <AveliosSpinner spinning={slotsLoading}>
        <Space
          style={{ width: '100%', height: '100%', marginTop: 22 }}
          direction={'vertical'}
          size={16}
        >
          {!directBooking && (
            <p className={"ant-list-item-meta-description description"}>
              {formatMessage({
                id: 'facility-selection.slot-selection.description',
              })}
            </p>
          )}
          {appointmentType ? (
            <div
              className={
                noAppointmentsAvailable ? styles.noAppointments : styles.nextAvailableAppointments
              }
            >
              {noAppointmentsAvailable && !slotsLoading ? (
                <span>
                  {formatMessage({
                    id: 'facility-selection.no-next-available-appointments',
                  })}
                </span>
              ) : (
                <Row gutter={[4, 8]}>
                  {distinctSlots.slice(0, 9).map((slot) => (
                    <Col
                      key={slot.startTimeSeconds}
                      xs={12}
                      md={10}
                      lg={8}
                      onClick={() =>
                        router.push({
                          pathname: '/slot-selection',
                          query: {
                            ...router.query,
                            clinicId,
                            facilityId,
                            officeHourId,
                            slot: slot.startTimeSeconds,
                            appointmentTypeId: appointmentType?.id,
                            appointmentLocation: appointmentLocation,
                          },
                        })
                      }
                    >
                      <div className={styles.availableAppointment}>
                        {moment(new Date(slot.startTimeSeconds * 1000)).format(
                          "DD.MM.YY | HH:mm"
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          ) : (
            <div>{formatMessage({ id: 'facility-selection.select-type' })}</div>
          )}
          {officeHourId && (
            <div>
              <AveliosButton
                type={'primary'}
                size={'large'}
                block
                onClick={() => {
                  router.push({
                    pathname: 'slot-selection',
                    query: {
                      ...router.query,
                      facilityId,
                      clinicId,
                      officeHourId,
                      appointmentTypeId: appointmentType?.id,
                      appointmentLocation: appointmentLocation,
                    },
                  });
                }}
              >
                {formatMessage({
                  id: 'action.see-more-appointment-proposals',
                })}
              </AveliosButton>
            </div>
          )}
        </Space>
      </AveliosSpinner>
    </div>
  );
};
